import React, { useState } from "react";
import ClientNavbar from "../layout/ClientNavbar";
import { NavLink, useNavigate } from "react-router-dom";
import {
    FiPlus,
    FiSearch,
    FiFilter,
    FiEye,
    FiClock,
    FiCheckCircle,
    FiXCircle,
    FiAlertCircle,
    FiFileText,
    FiPaperclip,
} from "react-icons/fi";

// Format date
const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

// Format time
const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
    });
};

export default function TicketsList() {
    const navigate = useNavigate();
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [rateLimitReached, setRateLimitReached] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        subject: "",
        category: "",
        priority: "medium",
        description: "",
        attachments: [],
    });

    // Static data - Tickets
    const tickets = [
        {
            id: "TKT-001",
            subject: "Account Statement Download Issue",
            category: "Technical",
            priority: "high",
            status: "open",
            createdAt: "2024-01-20T10:30:00",
            updatedAt: "2024-01-22T14:20:00",
            lastReply: "2024-01-22T14:20:00",
            replies: 3,
            attachments: 2,
        },
        {
            id: "TKT-002",
            subject: "KYC Document Verification",
            category: "Account",
            priority: "medium",
            status: "in_progress",
            createdAt: "2024-01-18T09:15:00",
            updatedAt: "2024-01-22T11:45:00",
            lastReply: "2024-01-22T11:45:00",
            replies: 5,
            attachments: 1,
        },
        {
            id: "TKT-003",
            subject: "Portfolio Overview Data Mismatch",
            category: "Data",
            priority: "low",
            status: "resolved",
            createdAt: "2024-01-15T16:00:00",
            updatedAt: "2024-01-20T10:00:00",
            lastReply: "2024-01-20T10:00:00",
            replies: 4,
            attachments: 0,
        },
        {
            id: "TKT-004",
            subject: "Research Subscription Renewal",
            category: "Billing",
            priority: "medium",
            status: "closed",
            createdAt: "2024-01-10T12:30:00",
            updatedAt: "2024-01-18T15:30:00",
            lastReply: "2024-01-18T15:30:00",
            replies: 2,
            attachments: 0,
        },
    ];

    const getStatusConfig = (status) => {
        const configs = {
            open: {
                color: "#ef4444",
                bg: "#fee2e2",
                icon: FiAlertCircle,
                label: "Open",
            },
            in_progress: {
                color: "#f59e0b",
                bg: "#fef3c7",
                icon: FiClock,
                label: "In Progress",
            },
            resolved: {
                color: "#10b981",
                bg: "#d1fae5",
                icon: FiCheckCircle,
                label: "Resolved",
            },
            closed: {
                color: "#6b7280",
                bg: "#f3f4f6",
                icon: FiXCircle,
                label: "Closed",
            },
        };
        return configs[status] || configs.open;
    };

    const getPriorityConfig = (priority) => {
        const configs = {
            high: { color: "#ef4444", label: "High" },
            medium: { color: "#f59e0b", label: "Medium" },
            low: { color: "#10b981", label: "Low" },
        };
        return configs[priority] || configs.medium;
    };

    const filteredTickets = tickets.filter((ticket) => {
        const matchesSearch =
            ticket.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
            ticket.id.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesStatus = statusFilter === "all" || ticket.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        const allowedTypes = ["image/jpeg", "image/png", "image/jpg", "application/pdf"];
        const maxSize = 5 * 1024 * 1024; // 5MB

        const validFiles = files.filter((file) => {
            if (!allowedTypes.includes(file.type)) {
                alert(`File type ${file.type} is not allowed. Only JPEG, PNG, and PDF files are allowed.`);
                return false;
            }
            if (file.size > maxSize) {
                alert(`File ${file.name} exceeds 5MB size limit.`);
                return false;
            }
            return true;
        });

        setFormData((prev) => ({
            ...prev,
            attachments: [...prev.attachments, ...validFiles],
        }));
    };

    const removeAttachment = (index) => {
        setFormData((prev) => ({
            ...prev,
            attachments: prev.attachments.filter((_, i) => i !== index),
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        // Simulate rate limit check
        if (rateLimitReached) {
            alert("Rate limit reached. Please try again later.");
            setIsSubmitting(false);
            return;
        }

        // Simulate API call
        setTimeout(() => {
            alert("Ticket created successfully!");
            setFormData({
                subject: "",
                category: "",
                priority: "medium",
                description: "",
                attachments: [],
            });
            setShowCreateForm(false);
            setIsSubmitting(false);
        }, 1000);
    };

    const handleViewTicket = (ticketId) => {
        navigate(`/support/ticket-detail/${ticketId}`);
    };

    return (
        <>
            <ClientNavbar />
            <div className="page-content" style={{ background: "#f8fafc" }}>
                    {/* Header */}
                    <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: "12px" }}>
                        <div className="card-body p-2">
                            <div className="d-flex justify-content-between align-items-center flex-wrap">
                                <div>
                                    <h3 className="text-primary mb-2">Tickets</h3>
                                    <nav
                                        aria-label="breadcrumb"
                                        style={{ "--bs-breadcrumb-divider": "none" }}
                                    >
                                        <ol className="breadcrumb mb-0">
                                            <li className="breadcrumb-item">
                                                <NavLink to="/overview">Dashboard</NavLink> /
                                            </li>
                                            <li className="breadcrumb-item">
                                                <NavLink to="/support/tickets">Support</NavLink> /
                                            </li>
                                            <li className="breadcrumb-item active">Tickets</li>
                                        </ol>
                                    </nav>
                                </div>
                                <button
                                    className="btn btn-primary"
                                    onClick={() => setShowCreateForm(!showCreateForm)}
                                
                                >
                                    {/* <FiPlus className="me-2" /> */}
                                    {showCreateForm ? "Cancel" : "Create Ticket"}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Create Ticket Form */}
                    {showCreateForm && (
                        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: "12px", overflow: "hidden" }}>
                            <div
                                className="card-header border-0 py-3 px-4"
                                style={{
                                    background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                                    borderBottom: "1px solid #b3eaf4",
                                }}
                            >
                                <div className="d-flex align-items-center">
                                    <div
                                        className="me-3 d-flex align-items-center justify-content-center"
                                        style={{
                                            width: "40px",
                                            height: "40px",
                                            borderRadius: "10px",
                                            background: "#c8eeecbf",
                                        }}
                                    >
                                        <FiPlus style={{ color: "#39b2b4", fontSize: "20px" }} />
                                    </div>
                                    <h5 className="mb-0 " >
                                        Create New Ticket
                                    </h5>
                                </div>
                            </div>
                            <div className="card-body p-4">
                                <form onSubmit={handleSubmit}>
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold mb-2" style={{ color: "#006e82" }}>
                                                Subject <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={formData.subject}
                                                onChange={(e) =>
                                                    setFormData((prev) => ({ ...prev, subject: e.target.value }))
                                                }
                                                required
                                                style={{
                                                    borderRadius: "10px",
                                                    borderColor: "#b3eaf4",
                                                    fontSize: "14px",
                                                }}
                                                onFocus={(e) => {
                                                    e.target.style.borderColor = "#006e82";
                                                    e.target.style.boxShadow = "0 0 0 0.2rem rgba(0, 110, 130, 0.1)";
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.borderColor = "#b3eaf4";
                                                    e.target.style.boxShadow = "none";
                                                }}
                                            />
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label fw-semibold mb-2" style={{ color: "#006e82" }}>
                                                Category <span className="text-danger">*</span>
                                            </label>
                                            <select
                                                className="form-select"
                                                value={formData.category}
                                                onChange={(e) =>
                                                    setFormData((prev) => ({ ...prev, category: e.target.value }))
                                                }
                                                required
                                                style={{
                                                    borderRadius: "10px",
                                                    borderColor: "#b3eaf4",
                                                    fontSize: "14px",
                                                }}
                                                onFocus={(e) => {
                                                    e.target.style.borderColor = "#006e82";
                                                    e.target.style.boxShadow = "0 0 0 0.2rem rgba(0, 110, 130, 0.1)";
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.borderColor = "#b3eaf4";
                                                    e.target.style.boxShadow = "none";
                                                }}
                                            >
                                                <option value="">Select Category</option>
                                                <option value="Technical">Technical</option>
                                                <option value="Account">Account</option>
                                                <option value="Billing">Billing</option>
                                                <option value="Data">Data</option>
                                                <option value="Other">Other</option>
                                            </select>
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label fw-semibold mb-2" style={{ color: "#006e82" }}>
                                                Priority
                                            </label>
                                            <select
                                                className="form-select"
                                                value={formData.priority}
                                                onChange={(e) =>
                                                    setFormData((prev) => ({ ...prev, priority: e.target.value }))
                                                }
                                                style={{
                                                    borderRadius: "10px",
                                                    borderColor: "#b3eaf4",
                                                    fontSize: "14px",
                                                }}
                                                onFocus={(e) => {
                                                    e.target.style.borderColor = "#006e82";
                                                    e.target.style.boxShadow = "0 0 0 0.2rem rgba(0, 110, 130, 0.1)";
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.borderColor = "#b3eaf4";
                                                    e.target.style.boxShadow = "none";
                                                }}
                                            >
                                                <option value="low">Low</option>
                                                <option value="medium">Medium</option>
                                                <option value="high">High</option>
                                            </select>
                                        </div>
                                        <div className="col-12">
                                            <label className="form-label fw-semibold mb-2" style={{ color: "#006e82" }}>
                                                Description <span className="text-danger">*</span>
                                            </label>
                                            <textarea
                                                className="form-control"
                                                rows="5"
                                                value={formData.description}
                                                onChange={(e) =>
                                                    setFormData((prev) => ({ ...prev, description: e.target.value }))
                                                }
                                                required
                                                placeholder="Please provide detailed information about your issue..."
                                                style={{
                                                    borderRadius: "10px",
                                                    borderColor: "#b3eaf4",
                                                    fontSize: "14px",
                                                }}
                                                onFocus={(e) => {
                                                    e.target.style.borderColor = "#006e82";
                                                    e.target.style.boxShadow = "0 0 0 0.2rem rgba(0, 110, 130, 0.1)";
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.borderColor = "#b3eaf4";
                                                    e.target.style.boxShadow = "none";
                                                }}
                                            />
                                        </div>
                                        <div className="col-12">
                                            <label className="form-label fw-semibold mb-2" style={{ color: "#006e82" }}>
                                                Attachments (Optional)
                                            </label>
                                            <input
                                                type="file"
                                                className="form-control"
                                                multiple
                                                accept="image/jpeg,image/png,image/jpg,application/pdf"
                                                onChange={handleFileChange}
                                                style={{
                                                    borderRadius: "10px",
                                                    borderColor: "#b3eaf4",
                                                    fontSize: "14px",
                                                }}
                                            />
                                            <div className="form-text mt-2" style={{ fontSize: "12px", color: "#64748b" }}>
                                                Allowed file types: JPEG, PNG, PDF. Maximum file size: 5MB per file.
                                            </div>
                                            {formData.attachments.length > 0 && (
                                                <div className="mt-3">
                                                    {formData.attachments.map((file, index) => (
                                                        <div
                                                            key={index}
                                                            className="d-flex align-items-center justify-content-between p-2 mb-2 rounded"
                                                            style={{
                                                                backgroundColor: "#ccf1f7",
                                                                border: "1px solid #b3eaf4",
                                                            }}
                                                        >
                                                            <div className="d-flex align-items-center">
                                                                <FiPaperclip className="me-2" style={{ color: "#006e82" }} />
                                                                <span style={{ fontSize: "13px", color: "#006e82" }}>
                                                                    {file.name} ({(file.size / 1024).toFixed(2)} KB)
                                                                </span>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm"
                                                                onClick={() => removeAttachment(index)}
                                                                style={{
                                                                    color: "#ef4444",
                                                                    background: "transparent",
                                                                    border: "none",
                                                                }}
                                                            >
                                                                <FiXCircle />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="d-flex gap-2 mt-4">
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? "Submitting..." : "Submit Ticket"}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn fw-semibold"
                                            onClick={() => setShowCreateForm(false)}
                                            style={{
                                                background: "linear-gradient(135deg, #39b2b4 0%, #06b6a4 100%)",
                                                color: "white",
                                                border: "none",
                                                padding: "10px 24px",
                                            }}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}

                    {/* Search and Filters */}
                    <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: "12px", overflow: "hidden" }}>
                        <div
                            className="card-header border-0 py-3 px-4"
                            style={{
                                background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                                borderBottom: "1px solid #b3eaf4",
                            }}
                        >
                            <div className="d-flex align-items-center justify-content-between flex-wrap">
                                <div className="d-flex align-items-center">
                                    <div
                                        className="me-3 d-flex align-items-center justify-content-center"
                                        style={{
                                            width: "40px",
                                            height: "40px",
                                            borderRadius: "10px",
                                            background: "#c8eeecbf",
                                        }}
                                    >
                                        <FiFilter style={{ color: "#39b2b4", fontSize: "20px" }} />
                                    </div>
                                    <h6 className="mb-0 fw-bold text-dark">Filter Tickets</h6>
                                </div>
                                <div className="mt-2 mt-md-0">
                                    <span className="badge bg-light text-dark px-3 py-2 fw-semibold" style={{ fontSize: "12px" }}>
                                        {filteredTickets.length} {filteredTickets.length === 1 ? "Ticket" : "Tickets"}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="card-body p-4">
                            <div className="row g-3">
                                <div className="col-md-8">
                                    <div className="input-group">
                                        <span
                                            className="input-group-text border-end-0"
                                            style={{
                                                backgroundColor: "#ccf1f7",
                                                borderColor: "#b3eaf4",
                                                borderRadius: "10px 0 0 10px",
                                            }}
                                        >
                                            <FiSearch style={{ color: "#006e82", fontSize: "18px" }} />
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control border-start-0"
                                            placeholder="Search by subject or ticket ID..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            style={{
                                                borderRadius: "0 10px 10px 0",
                                                borderColor: "#b3eaf4",
                                                fontSize: "14px",
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.borderColor = "#006e82";
                                                e.target.style.boxShadow = "0 0 0 0.2rem rgba(0, 110, 130, 0.1)";
                                            }}
                                            onBlur={(e) => {
                                                e.target.style.borderColor = "#b3eaf4";
                                                e.target.style.boxShadow = "none";
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <select
                                        className="form-select"
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                        style={{
                                            borderRadius: "10px",
                                            borderColor: "#b3eaf4",
                                            fontSize: "14px",
                                        }}
                                        onFocus={(e) => {
                                            e.target.style.borderColor = "#006e82";
                                            e.target.style.boxShadow = "0 0 0 0.2rem rgba(0, 110, 130, 0.1)";
                                        }}
                                        onBlur={(e) => {
                                            e.target.style.borderColor = "#b3eaf4";
                                            e.target.style.boxShadow = "none";
                                        }}
                                    >
                                        <option value="all">All Status</option>
                                        <option value="open">Open</option>
                                        <option value="in_progress">In Progress</option>
                                        <option value="resolved">Resolved</option>
                                        <option value="closed">Closed</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Rate Limit Notice */}
                    {rateLimitReached && (
                        <div
                            className="alert mb-4 border-0"
                            style={{
                                borderRadius: "12px",
                                backgroundColor: "#fee2e2",
                                border: "1px solid #ef4444",
                                color: "#991b1b",
                            }}
                        >
                            <div className="d-flex align-items-start">
                                <FiAlertCircle className="me-3 mt-1" style={{ fontSize: "24px", color: "#ef4444" }} />
                                <div>
                                    <strong style={{ fontSize: "16px" }}>Rate Limit Reached</strong>
                                    <div className="mt-2" style={{ fontSize: "14px" }}>
                                        You have reached the maximum number of tickets you can create. Please try again later.
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Tickets Table */}
                    <div className="card shadow-sm border-0" style={{ borderRadius: "12px", overflow: "hidden" }}>
                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <table className="table table-hover mb-0">
                                    <thead style={{ backgroundColor: "#ccf1f7" }}>
                                        <tr>
                                            <th className="fw-semibold" style={{ padding: "16px 20px", color: "#006e82" }}>
                                                Ticket ID
                                            </th>
                                            <th className="fw-semibold" style={{ padding: "16px 20px", color: "#006e82" }}>
                                                Subject
                                            </th>
                                            <th className="fw-semibold" style={{ padding: "16px 20px", color: "#006e82" }}>
                                                Category
                                            </th>
                                            <th className="fw-semibold" style={{ padding: "16px 20px", color: "#006e82" }}>
                                                Priority
                                            </th>
                                            <th className="fw-semibold" style={{ padding: "16px 20px", color: "#006e82" }}>
                                                Status
                                            </th>
                                            <th className="fw-semibold" style={{ padding: "16px 20px", color: "#006e82" }}>
                                                Last Updated
                                            </th>
                                            <th className="fw-semibold text-center" style={{ padding: "16px 20px", color: "#006e82" }}>
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredTickets.length === 0 ? (
                                            <tr>
                                                <td colSpan="7" className="text-center py-5" style={{ color: "#64748b" }}>
                                                    No tickets found matching your criteria.
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredTickets.map((ticket) => {
                                                const statusConfig = getStatusConfig(ticket.status);
                                                const priorityConfig = getPriorityConfig(ticket.priority);
                                                const StatusIcon = statusConfig.icon;

                                                return (
                                                    <tr
                                                        key={ticket.id}
                                                        style={{
                                                            transition: "background-color 0.2s ease",
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.backgroundColor = "#f0f9ff";
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.backgroundColor = "transparent";
                                                        }}
                                                    >
                                                        <td style={{ padding: "16px 20px" }}>
                                                            <strong style={{ color: "#006e82" }}>{ticket.id}</strong>
                                                        </td>
                                                        <td style={{ padding: "16px 20px" }}>
                                                            <div style={{ color: "#006e82", fontWeight: "500" }}>
                                                                {ticket.subject}
                                                            </div>
                                                            <div className="d-flex align-items-center gap-3 mt-1">
                                                                <small style={{ color: "#64748b", fontSize: "12px" }}>
                                                                    {/* <FiClock className="me-1" style={{ fontSize: "11px" }} /> */}
                                                                    {formatDate(ticket.createdAt)}
                                                                </small>
                                                                {ticket.attachments > 0 && (
                                                                    <small style={{ color: "#64748b", fontSize: "12px" }}>
                                                                        {/* <FiPaperclip className="me-1" style={{ fontSize: "11px" }} /> */}
                                                                        {ticket.attachments}
                                                                    </small>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td style={{ padding: "16px 20px" }}>
                                                            <span
                                                                className="badge px-3 py-2"
                                                                style={{
                                                                    fontSize: "12px",
                                                                    backgroundColor: "#39b2b4",
                                                                    color: "white",
                                                                }}
                                                            >
                                                                {ticket.category}
                                                            </span>
                                                        </td>
                                                        <td style={{ padding: "16px 20px" }}>
                                                            <span
                                                                className="badge px-3 py-2"
                                                                style={{
                                                                    fontSize: "12px",
                                                                    backgroundColor: priorityConfig.color + "20",
                                                                    color: priorityConfig.color,
                                                                    border: `1px solid ${priorityConfig.color}40`,
                                                                }}
                                                            >
                                                                {priorityConfig.label}
                                                            </span>
                                                        </td>
                                                        <td style={{ padding: "16px 20px" }}>
                                                            <span
                                                                className="badge px-3 py-2 d-inline-flex align-items-center"
                                                                style={{
                                                                    fontSize: "12px",
                                                                    backgroundColor: statusConfig.bg,
                                                                    color: statusConfig.color,
                                                                }}
                                                            >
                                                                <StatusIcon className="me-1" style={{ fontSize: "12px" }} />
                                                                {statusConfig.label}
                                                            </span>
                                                        </td>
                                                        <td style={{ padding: "16px 20px", color: "#64748b", fontSize: "13px" }}>
                                                            {formatDate(ticket.updatedAt)} {formatTime(ticket.updatedAt)}
                                                        </td>
                                                        <td className="text-center" style={{ padding: "16px 20px" }}>
                                                            <button
                                                                className="btn btn-sm fw-semibold"
                                                                onClick={() => handleViewTicket(ticket.id)}
                                                                style={{
                                                                    borderRadius: "8px",
                                                                    backgroundColor: "#39b2b4",
                                                                    color: "white",
                                                                    border: "none",
                                                                    padding: "16px",
                                                                }}
                                                            >
                                                                <FiEye className="" />
                                                                {/* View */}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
        </>
    );
}
